import { useContext, useEffect, useState } from "react";
import { Form, Input, Button, Select, Modal, Tooltip } from "antd";
import { Edit2 } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  getExecutiveEmployeeList,
  updateLeadDetails,
  transferLeads,
  getLeadStatusList,
  getUnqualifiedReasons,
} from "../../actionCreator/leadListingActionCreater";
import { getAllUsers } from "../../actionCreator/userManagementActionCreator";
import { useAlert } from "../../context/AlertProvider";
import { getUserInfo } from "../../utils/utils";
import {
  formatAndExtractCountryCode,
  handleMobileChange,
  prefixSelector,
} from "../../utils/mobileValidation";
import { countries } from "../../constant/Countries";
import PlaceAutocomplete from "../../common/PlaceAutocomplete";
import { AuthContext } from "../../context/AuthContext";
import { userRoles } from "../../config";
const { TextArea } = Input;

const UpdateLeadDetails = ({ lead }) => {
  const { showAlert } = useAlert();
  const { isLeadEdit } = useContext(AuthContext);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const showModal = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (isLeadEdit) {
      setIsModalOpen(true);
    } else {
      showAlert(
        "warning",
        "Permission Denied",
        "You do not have permission to update lead.",
      );
    }
  };

  const handleCancel = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsModalOpen(false);
  };

  return (
    <>
      <Tooltip title="Edit Lead">
        <button
          type="button"
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            showModal(e);
          }}
          className="tw-flex tw-items-center tw-justify-center tw-size-7 tw-rounded-lg hover:tw-bg-slate-100 tw-text-slate-450 hover:tw-text-theme tw-cursor-pointer tw-transition-all tw-border-none tw-bg-transparent tw-outline-none"
        >
          <Edit2 size={16} />
        </button>
      </Tooltip>
      {isModalOpen && (
        <Modal
          title={null}
          className="delete-popup"
          open={isModalOpen}
          centered
          footer={null}
          onOk={handleCancel}
          onCancel={handleCancel}
          destroyOnClose
        >
          <div className="tw-bg-slate-50 tw-text-slate-800 tw-text-lg tw-font-bold tw-py-4 tw-px-6 tw-rounded-t-2xl tw-border tw-border-solid tw-border-slate-100/50">
            Lead Details
          </div>
          <LeadForm lead={lead} handleCancel={handleCancel} />
        </Modal>
      )}
    </>
  );
};

export default UpdateLeadDetails;

const LeadForm = ({ lead, handleCancel }) => {
  const dispatch = useDispatch();
  const { showAlert } = useAlert();
  const [searchParams] = useSearchParams();
  const [form] = Form.useForm();
  const { id: userId } = getUserInfo();
  const [status, setStatus] = useState();
  const [reason, setReason] = useState();
  const [error, setError] = useState();
  const leadStatusListing = useSelector(
    (state) => state.leadListingReducer.leadStatusList,
  );
  const unqualifiedReasons = useSelector(
    (state) => state.leadListingReducer.unqualifiedReasons,
  );
  const currentUser = getUserInfo();
  const canTransferLead =
    lead?.allowedActions?.includes("TRANSFER") ||
    currentUser?.role === userRoles?.admin;
  const usersData = useSelector((state) => state.UserManagementReducer.users);
  const users = Array.isArray(usersData) ? usersData : usersData?.content || [];

  useEffect(() => {
    if (!users || !users.length) {
      dispatch(
        getAllUsers({ sort: "firstName,asc", size: 1000, noLoading: true }),
      );
    }
  }, [dispatch, users?.length]);

  useEffect(() => {
    const currentOwnerId =
      lead?.ownerId ||
      lead?.employeeId ||
      (typeof lead?.employee === "object"
        ? lead?.employee?.id
        : lead?.employee);

    const rawPhone = String(lead?.phoneNo || lead?.mobile || "");
    const phoneVal = rawPhone ? rawPhone.split("/")?.[0] : "";
    const phoneDetails = formatAndExtractCountryCode(phoneVal);

    const statusMatch = leadStatusListing?.find(
      (d) => d.id === lead?.status || d.text === lead?.status,
    );
    const statusText = statusMatch?.text || lead?.status;

    form.setFieldsValue({
      ...lead,
      firstname: lead?.firstname || lead?.fullname?.split(" ")?.[0] || "",
      lastname:
        lead?.lastname || lead?.fullname?.split(" ")?.slice(1)?.join(" ") || "",
      ownerId: currentOwnerId,
      unqualifiedReason:
        lead?.unqualifiedReason?.reason || lead?.unqualifiedReason,
      status: statusText ? statusText : undefined,
      phoneNo: phoneDetails?.numberWithoutCountryCode,
      prefix: phoneDetails?.countryLabel,
    });
    setStatus(statusText);
    setReason(lead?.unqualifiedReason?.reason || lead?.unqualifiedReason);
  }, [lead, form, leadStatusListing]);

  const onFinish = async (values) => {
    if (values?.ownerId) {
      const currentOwnerId =
        lead?.ownerId ||
        lead?.employeeId ||
        (typeof lead?.employee === "object"
          ? lead?.employee?.id
          : lead?.employee);
      if (values.ownerId !== currentOwnerId) {
        await dispatch(
          transferLeads({
            leadIds: [lead?.id],
            newOwnerId: values.ownerId,
          }),
        );
      }
    }

    const selectedPrefixObj = countries?.find(
      (d) => d?.label === values?.prefix,
    );
    const prefixCode = selectedPrefixObj ? selectedPrefixObj.code : "91";

    const res = await dispatch(
      updateLeadDetails({
        ...values,
        unqualifiedReason: {
          id: unqualifiedReasons?.find(
            (d) => d?.reason === values?.unqualifiedReason,
          )?.id,
          reason: values?.unqualifiedReason,
        },
        fullname: (
          (values?.firstname || "") +
          " " +
          (values?.lastname || "")
        ).trim(),
        status:
          leadStatusListing?.find((d) => d.text === values?.status)?.id ||
          values?.status,
        id: lead?.id,
        userId,
        phoneNo: values?.phoneNo
          ? "+" + prefixCode + " " + values?.phoneNo
          : lead?.phoneNo || lead?.mobile,
      }),
    );
    if (res?.status === 200 || res?.data) {
      showAlert(
        "success",
        `${res?.data?.firstname || values?.firstname || ""} ${res?.data?.lastname || values?.lastname || ""} Lead updated successfully!`.trim(),
      );

      const allParams = {};
      for (const [key, value] of searchParams.entries()) {
        allParams[key] = value;
      }

      dispatch(
        getExecutiveEmployeeList(
          {
            ...allParams,
            page: allParams?.page ? allParams?.page - 1 : 0,
          },
          undefined,
          true,
        ),
      );

      handleCancel();
      form.resetFields();
    } else {
      if (res?.response?.status === 403) {
        showAlert(
          "warning",
          "Permission Denied",
          "You do not have permission to update lead.",
        );
        handleCancel();
        form.resetFields();
        setError();
      } else {
        showAlert(
          "error",
          res?.response?.data?.title || "Failed to update lead",
        );
        setError(res?.response?.data?.title || "Failed to update lead");
      }
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={{
        ...lead,
        status: {
          label: leadStatusListing?.find((d) => d.id === lead?.status)?.text,
          value: leadStatusListing?.find((d) => d.id === lead?.status)?.text,
        },
      }}
      onFinish={onFinish}
      className="tw-max-w-[600px] tw-mx-auto"
    >
      <div className="tw-p-6">
        {error && <p className="text-error text-center pb-2">{error}</p>}
        <Form.Item
          name="firstname"
          label="First Name"
          normalize={(value) => value?.replace(/^\s+/, "")}
          rules={[{ required: true, message: "Please enter first name" }]}
        >
          <Input className="common-input" />
        </Form.Item>
        <Form.Item
          name="lastname"
          label="Last Name"
          normalize={(value) => value?.replace(/^\s+/, "")}
          rules={[{ required: true, message: "Please enter last name" }]}
        >
          <Input className="common-input" />
        </Form.Item>

        <Form.Item
          name="phoneNo"
          label="Mobile"
          rules={[{ required: true, message: "Please enter mobile number." }]}
        >
          <Input
            addonBefore={prefixSelector()}
            className="common-input"
            maxLength={20}
            onChange={(e) => handleMobileChange(e.target.value, form)}
          />
        </Form.Item>

        <Form.Item
          name="companyName"
          label="Company Name"
          normalize={(value) => value?.replace(/^\s+/, "")}
          rules={[{ required: true, message: "Please enter company name." }]}
        >
          <PlaceAutocomplete
            onPlaceSelect={(v) => {
              form.setFieldsValue({
                companyName: v?.name,
                website: v?.website,
              });
            }}
          />
        </Form.Item>
        <Form.Item name="website" label="Website">
          <Input className="common-input" />
        </Form.Item>
        <Form.Item name="ownerId" label="Lead Owner">
          <Select
            placeholder="Select Lead Owner"
            disabled={!canTransferLead}
            allowClear
            options={users.map((u) => ({
              label:
                `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.login,
              value: u.id,
            }))}
          />
        </Form.Item>
        <Form.Item
          name="status"
          label="Status"
          normalize={(value) => value?.replace(/^\s+/, "")}
          rules={[{ required: true, message: "Please select status." }]}
        >
          <Select
            options={leadStatusListing?.map((d) => ({
              label: d.text,
              value: d.text,
            }))}
            onChange={(value) => {
              form.setFieldsValue({
                status: value,
              });
              setStatus(value);
            }}
          />
        </Form.Item>
        {status === "Closed Lost" && (
          <>
            <Form.Item
              name="lostReason"
              label="Lost Reason"
              rules={[{ required: true, message: "Please add a reason" }]}
            >
              <Input className="common-input" placeholder="Enter reason" />
            </Form.Item>

            <Form.Item name="comments" label="Comments">
              <TextArea
                className="common-input"
                rows={3}
                placeholder="Enter comments"
              />
            </Form.Item>
          </>
        )}

        {status === "Un-Qualified" && (
          <>
            <Form.Item
              name="unqualifiedReason"
              label="Reason for Unqualification"
              rules={[{ required: true, message: "Please select a reason" }]}
            >
              <Select
                value={reason}
                placeholder="Select reason"
                options={unqualifiedReasons?.map((d) => ({
                  label: d.reason,
                  value: d.reason,
                }))}
                onChange={(value) => {
                  form.setFieldsValue({
                    unqualifiedReason: value,
                  });
                  setReason(value);
                }}
              />
            </Form.Item>

            {reason === "Others" && (
              <Form.Item
                name="unqualifiedReasonsOthers"
                label="Add Reason"
                rules={[{ required: true, message: "Please add a reason" }]}
              >
                <TextArea
                  className="common-input"
                  rows={3}
                  placeholder="Enter unqualification reason"
                />
              </Form.Item>
            )}
          </>
        )}
      </div>

      <div className="tw-flex tw-bg-slate-50 tw-rounded-b-2xl tw-py-4 tw-px-6 tw-items-center tw-justify-end tw-gap-3 tw-border tw-border-solid tw-border-slate-100/50">
        <Button
          className="!tw-h-[38px] !tw-rounded-lg !tw-border-slate-200 !tw-bg-white !tw-px-5 !tw-text-xs !tw-font-bold !tw-text-slate-500 hover:!tw-border-slate-300 hover:!tw-text-slate-700 tw-transition-all tw-duration-200"
          onClick={() => {
            handleCancel();
            form.resetFields();
          }}
        >
          Cancel
        </Button>
        <Button
          type="primary"
          className="!tw-h-[38px] !tw-rounded-lg !tw-bg-theme !tw-border-theme !tw-text-white tw-text-xs tw-font-bold tw-px-5 tw-shadow-sm hover:tw-shadow-md tw-transition-all tw-duration-200"
          htmlType="submit"
        >
          Update
        </Button>
      </div>
    </Form>
  );
};
